import { ApolloClient, from, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { type PublicClientApplication } from '@azure/msal-browser';
import { createUploadLink } from 'apollo-upload-client';
import type { NormalizedCacheObject } from '@apollo/client';
import type { default as CreateUploadLinkFn } from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';
import { Kind, OperationTypeNode } from 'graphql';
import getToken from '@auth/msal/get-token';
import correlationIdLink from './utils/correlationId';
import errorLink from './utils/errorLink';
import backendVersionLink from './utils/backend-version-link';
import rateLimitLink from './utils/rate-limit-link';

const typedCreateUploadLink = createUploadLink as typeof CreateUploadLinkFn;

function isHeadersRecord(value: unknown): value is Record<string, string> {
    return typeof value === 'object' && value !== null;
}

const toWsUri = (httpUri = ''): string =>
    (httpUri || '').replace(/^http/, 'ws');

export default function createApolloClient(
    msal: PublicClientApplication,
    apiUri: string,
): ApolloClient<NormalizedCacheObject> {
    const httpLink = typedCreateUploadLink({
        uri: apiUri,
    });

    const wsLink = new GraphQLWsLink(
        createClient({
            url: toWsUri(apiUri),
            keepAlive: 10_000,
            connectionParams: async () => {
                const token = await getToken(msal);
                return token ? { Authorization: `Bearer ${token}` } : {};
            },
        }),
    );

    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === Kind.OPERATION_DEFINITION &&
                definition.operation === OperationTypeNode.SUBSCRIPTION
            );
        },
        wsLink,
        httpLink,
    );

    const authLink = setContext(async (_, prevContext) => {
        const token = await getToken(msal);
        const headers = isHeadersRecord(prevContext.headers)
            ? prevContext.headers
            : {};
        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : null,
            },
        };
    });

    return new ApolloClient({
        link: from([
            correlationIdLink,
            backendVersionLink,
            errorLink,
            authLink,
            rateLimitLink,
            splitLink,
        ]),
        cache: new InMemoryCache({
            typePolicies: {
                ProjectComponentNotification: {
                    keyFields: ['assetItemID', 'notificationID', 'componentID'],
                },
                Query: {
                    fields: {
                        projects: {
                            keyArgs: ['where', 'orderBy'],
                        },
                    },
                },
            },
        }),
        defaultOptions: {
            query: {
                fetchPolicy: 'cache-first',
            },
        },
    });
}
